import { compare as createPatch } from 'fast-json-patch';
import { type Team, type TeamCustomRoles } from 'launchdarkly-api-typescript';
import qs from 'query-string';
import { type Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import config from '../config';
import { accountListingsRoot, accountsRoot, request as gonfalonRequest, type ILinks, LD_API_VERSION } from '../gonfalon';
import { request as baseRequest, type Metadata, type Series } from '../shared';
import { getStatusFromError } from '../shared/utils';

import {
  type Account,
  type AuditLogEntry,
  type AuditLogEntryDetail,
  type CatfoodFlagKeys,
  type CatfoodProjectKeys,
  type CustomRole,
  type Environment,
  type EnvironmentSummary,
  type GeneralAccountInfoResponse,
  type GeneralAccountInfoResponseRaw,
  type GeneralAccountSection,
  type IEapSegmentsResponse,
  type LegacySubscription,
  type Member,
  type MemberSessions,
  type MetricType,
  type OwnerPromotion,
  type Plan,
  type Project,
  type SamlConfig,
  type Subscription,
  type SubscriptionUsage,
} from './model';
import {
  account as accountSchema,
  accounts as accountsSchema,
  customRoles as customRolesSchema,
  memberSessions as memberSessionsSchema,
  members as membersSchema,
  teams as teamsSchema,
} from './schema';
import { isObjectIdHex } from './utils';

// Export types that are used by hooks and other modules
export type {
  Subscription,
  SubscriptionUsage,
  IEapSegmentsResponse,
  GeneralAccountInfoResponse,
  AuditLogEntryDetail,
};

// Local aliases to minimize edit churn below
const gonfalon = { request: gonfalonRequest, accountsRoot, accountListingsRoot } as const;
const schema = {
  account: accountSchema,
  accounts: accountsSchema,
  members: membersSchema,
  teams: teamsSchema,
  memberSessions: memberSessionsSchema,
  customRoles: customRolesSchema,
} as const;

export type AccountListResponse = Readonly<{
  entities: {
    accounts: {
      [id: string]: Account;
    };
  };
  result: {
    _links: ILinks;
    items: string[];
  };
}>;

export type AccountResponse = Readonly<{
  entities: {
    accounts: {
      [id: string]: Account;
    };
  };
}>;

const fetchEndpoint = ({ q, unpaid, overage }: { q?: string; unpaid?: boolean; overage?: MetricType | 'any' }) => {
  const search = qs.stringify({ q, unpaid: unpaid ? 1 : undefined, overage });
  return `${gonfalon.accountListingsRoot}?${search}`;
};

export const getAccounts = ({ q, unpaid, overage }: { q?: string; unpaid?: boolean; overage?: MetricType | 'any' }) =>
  gonfalon.request<AccountListResponse>(
    fetchEndpoint({ q: q ? q.trim() : undefined, unpaid, overage }),
    schema.accounts,
  );

export const getAccount = (id: string) =>
  gonfalon.request<AccountResponse>(`${gonfalon.accountsRoot}/${id}`, schema.account);

export type CatfoodFlagResponse = {
  value: unknown;
};

export const fetchCatfoodFlag = (projectKey: CatfoodProjectKeys, flagKey: CatfoodFlagKeys, accountId: string) => {
  const req = {
    url: `${config.provisioning.host}/api/flags/${projectKey}/${flagKey}/${accountId}`,
    headers: { 'Content-Type': 'application/json' },
    timeout: 5000,
  };
  return baseRequest<CatfoodFlagResponse>(req);
};

export const getLegacyAccountSubscription = (account: Account) =>
  gonfalon.request<LegacySubscription>(account._links.subscription.href, undefined, 16000);

export const getAccountSubscription = (account: Account) =>
  gonfalon.request<Subscription>(`/private/billingv2/accounts/${account._id}/subscription`);

export const getAccountSubscriptionUsage = (account: Account) =>
  gonfalon.request<SubscriptionUsage>(`/private/billingv2/accounts/${account._id}/subscription/usage`);

export type MemberResponse = Readonly<{
  entities: {
    members: {
      [id: string]: Member;
    };
  };
  result: {
    totalCount: number;
    _links: {
      first: {
        href: string;
      };
      last: {
        href: string;
      };
      next: {
        href: string;
      };
      prev: {
        href: string;
      };
      self: {
        href: string;
      };
    };
  };
}>;

export const changeAccountSubscriptionDates = (account: Account, date: object) =>
  gonfalon.request<Subscription>({
    method: 'PUT',
    url: `/private/billingv2/accounts/${account._id}/subscription/dates`,
    body: date,
  });

export const changeEnterpriseTrialCampaignDates = (account: Account, date: { startTime: string; endTime: string }) =>
  gonfalon.request<Subscription>({
    method: 'PATCH',
    url: `/private/billingv2/accounts/${account._id}/subscription/enterprise-campaign`,
    body: date,
  });

export const changeGuardianTrialCampaignDates = (account: Account, date: { startTime: string; endTime: string }) =>
  gonfalon.request<Subscription>({
    method: 'PATCH',
    url: `/private/billingv2/accounts/${account._id}/subscription/guardian-campaign`,
    body: date,
  });

export const patchAccountSubscription = (
  account: Account,
  prevSubscription: Subscription,
  nextSubscription: Subscription,
) => {
  const patch = createPatch(prevSubscription, nextSubscription);
  return gonfalon.request<Subscription>({
    method: 'PATCH',
    url: `/private/billingv2/accounts/${account._id}/subscription`,
    body: patch,
  });
};

export const patchLegacyAccountSubscription = (
  account: Account,
  prevSubscription: LegacySubscription,
  nextSubscription: LegacySubscription,
) => {
  const patch = createPatch(prevSubscription, nextSubscription);
  return gonfalon.request<LegacySubscription>({
    method: 'PATCH',
    url: account._links.subscription.href,
    body: patch,
  });
};

export const cancelSubscription = (account: Account, endImmediately: boolean) => {
  const queryParam = endImmediately ? '?endImmediately=true' : '';
  return gonfalon.request<{}>(
    {
      method: 'DELETE',
      url: `/private/accounts/${account._id}/subscription${queryParam}`,
    },
    undefined,
    40000,
  );
};

export type AuditLogEntryResponse = Readonly<{
  items: AuditLogEntry[];
  _links: ILinks;
}>;

export const getAuditLogUrl = (
  accountId: string,
  nextUrl?: string,
  query?: string,
  spec?: string,
  member?: string,
  fuzzy?: boolean,
  date?: ReadonlyArray<Date | null>,
  actions?: readonly string[],
  beforeCursor?: number,
  afterCursor?: number,
) => {
  /* default to rollup to always display parent entries */
  const defaultUrl = `/private/accounts/${accountId}/auditlog?`;
  const after = date && date[0];
  const before = date && date[1];

  const searchParams = new URLSearchParams('rollup=1&limit=20'); // Maximum allowed by API

  if (nextUrl) {
    return nextUrl;
  }
  if (query) {
    searchParams.append('q', query);
  }
  if (spec) {
    searchParams.append('spec', spec);
  }
  // Actions only filter when scoped to a resource (spec); the backend ignores
  // them otherwise, so we mirror that here to keep the URL clean.
  if (actions && actions.length > 0 && spec) {
    searchParams.append('actions', actions.join(','));
  }
  if (member) {
    searchParams.append('member', member);
  }
  if (fuzzy) {
    searchParams.append('fuzzy', 'true');
  }
  if (afterCursor !== undefined) {
    searchParams.set('after', `${afterCursor}`);
  } else if (after) {
    searchParams.append('after', `${after}`);
  }
  // `before` is the list cursor: entries occurred strictly before this timestamp.
  // A deep link passes the entry's own date so that one request returns the page
  // containing it, instead of walking every newer page.
  if (beforeCursor !== undefined) {
    searchParams.set('before', `${beforeCursor}`);
  } else if (before) {
    searchParams.append('before', `${before}`);
  }
  return defaultUrl + searchParams.toString();
};

export const getAccountAuditLogEntries = ({
  account,
  nextUrl,
  query,
  spec,
  member,
  fuzzy,
  date,
  actions,
  beforeCursor,
  afterCursor,
}: {
  account: Account;
  nextUrl?: string;
  query?: string;
  spec?: string;
  member?: string;
  fuzzy?: boolean;
  date?: ReadonlyArray<Date | null>;
  actions?: readonly string[];
  beforeCursor?: number;
  afterCursor?: number;
}) => {
  const makeRequest = (fuzzyOverride?: boolean) =>
    gonfalon.request<AuditLogEntryResponse>({
      url: getAuditLogUrl(
        account._id,
        nextUrl,
        query,
        spec,
        member,
        fuzzyOverride,
        date,
        actions,
        beforeCursor,
        afterCursor,
      ),
      headers: {
        'X-LD-AccountId': account._id,
        'X-LD-EnvId': account._id,
        'X-LD-PrjId': account._id,
      },
    });

  const initialRequest = makeRequest(fuzzy);

  if (!fuzzy || nextUrl) {
    return initialRequest;
  }

  // If fuzzy search fails server-side, retry once with strict matching.
  return initialRequest.pipe(
    catchError((error: unknown) => {
      const status = getStatusFromError(error);
      if (status !== undefined && status >= 500) {
        return makeRequest(undefined);
      }
      return throwError(() => error);
    }),
  );
};

export type AuditLogEntryDetailResponse = AuditLogEntryDetail;

// Detail URL for a single audit log entry. Matches each entry's
// `_links.self.href` (see internal/reps2/auditlogreps/auditlog_rep.go), so a
// deep-linked entry that is not in the loaded list can still be fetched by id.
export const getAuditLogEntryDetailUrl = (entryId: string) => `/api/v2/auditlog/${encodeURIComponent(entryId)}`;

export const getAccountAuditLogEntryDetail = (account: Account, entryUrl: string) =>
  gonfalon.request<AuditLogEntryDetailResponse>({
    url: entryUrl,
    headers: {
      'X-LD-AccountId': account._id,
      'X-LD-EnvId': account._id,
      'X-LD-PrjId': account._id,
    },
  });

export type ActionAndDescription = { action?: string; description?: string };
export type ActionsByResource = Partial<Record<string, ActionAndDescription[]>>;

// Fetches the RBAC action map (keyed by resource kind) used to populate the
// audit log action filter. Uses the private endpoint so it works cross-origin
// (the shared /internal/actions endpoint is session-authed and same-origin only).
export const getActions = (account: Account) =>
  gonfalon.request<ActionsByResource>({
    url: '/private/actions',
    headers: {
      'X-LD-AccountId': account._id,
      'X-LD-EnvId': account._id,
      'X-LD-PrjId': account._id,
    },
  });

const getAccountMembersUrl = (account: Account, enablePaginationOfMembers: boolean, url?: string) => {
  const queryParamsForRoleAttributes = new URLSearchParams();

  // Only append if the URL doesn't already have the parameter
  if (!url || !url.includes('expand=roleAttributes')) {
    queryParamsForRoleAttributes.append('expand', 'roleAttributes');
  }

  const params = queryParamsForRoleAttributes.toString();
  let requestUrl;

  if (url) {
    if (params) {
      requestUrl = `${url}${url.includes('?') ? '&' : '?'}${params}`;
    } else {
      requestUrl = url;
    }
  } else {
    requestUrl = `/api/v2/members?${params}`;
  }

  if (enablePaginationOfMembers) {
    return {
      method: 'GET',
      url: `${requestUrl || '/api/v2/members?limit=20&offset=0'}`,
      headers: {
        'ld-api-version': 'beta',
        'X-LD-AccountId': account._id,
        'X-LD-EnvId': account._id,
        'X-LD-PrjId': account._id,
      },
    };
  }
  return account._links.members.href;
};

export const getAccountMembers = (account: Account, enablePaginationOfMembers: boolean, url?: string) =>
  gonfalon.request<MemberResponse>(getAccountMembersUrl(account, enablePaginationOfMembers, url), schema.members);

export type MemberSessionsResponse = Readonly<{
  entities: {
    memberSessions: {
      [id: string]: MemberSessions;
    };
  };
}>;

export const getAccountMemberSessions = (account: Account) =>
  gonfalon.request<MemberSessionsResponse>(
    {
      url: `/private/accounts/${account._id}/sessions`,
      headers: {
        'X-LD-AccountId': account._id,
        'X-LD-EnvId': account._id,
        'X-LD-PrjId': account._id,
      },
    },
    schema.memberSessions,
  );

export type ProjectsPage = Readonly<{
  items: Project[];
  totalCount: number;
  _links: ILinks;
}>;

export type ProjectsPageParams = Readonly<{
  /** Matches project name or key, case-insensitive. */
  query?: string;
  ids?: readonly string[];
  keys?: readonly string[];
  limit: number;
  offset?: number;
}>;

type ProjectRepV4 = Readonly<{
  _id: string;
  key: string;
  name: string;
  environments?: Readonly<{ items: Environment[]; totalCount: number }>;
}>;

type ProjectsRepV4 = Readonly<{ items: ProjectRepV4[]; totalCount: number; _links: ILinks }>;

const accountScopeHeaders = (account: Account) => ({
  'X-LD-AccountId': account._id,
  'X-LD-EnvId': account._id,
  'X-LD-PrjId': account._id,
});

const toProject = (rep: ProjectRepV4): Project => ({
  _id: rep._id,
  key: rep.key,
  name: rep.name,
  environments: rep.environments?.items ?? [],
  environmentsTotalCount: rep.environments?.totalCount ?? 0,
});

// Filters travel as `field:value` pairs joined by ','. The server decodes the
// query string once before splitting on ',' and ':', then unescapes the query
// value a second time, so that value is encoded twice: a ',' or '+' typed into
// the search box survives both passes. ids and keys are compared raw, so once.
const projectsFilter = ({ query, ids, keys }: ProjectsPageParams) =>
  [
    query ? `query:${encodeURIComponent(encodeURIComponent(query))}` : '',
    ids?.length ? `ids:${ids.map((id) => encodeURIComponent(id)).join('|')}` : '',
    keys?.length ? `keys:${keys.map((key) => encodeURIComponent(key)).join('|')}` : '',
  ]
    .filter(Boolean)
    .join(',');

/** One page of the account's projects (API v20240415), each with its first 20 environments inlined. */
export const getAccountProjects = (account: Account, params: ProjectsPageParams) => {
  const filter = projectsFilter(params);
  const search = [
    'expand=environments',
    'sort=name',
    `limit=${params.limit}`,
    `offset=${params.offset ?? 0}`,
    filter ? `filter=${filter}` : '',
  ]
    .filter(Boolean)
    .join('&');

  return gonfalon
    .request<ProjectsRepV4>({
      method: 'GET',
      url: `${account._links.projects.href}?${search}`,
      headers: { 'LD-Api-Version': LD_API_VERSION, ...accountScopeHeaders(account) },
    })
    .pipe(
      map(
        (rep): ProjectsPage => ({ items: rep.items.map(toProject), totalCount: rep.totalCount, _links: rep._links }),
      ),
    );
};

export type EnvironmentsPage = Readonly<{
  items: Environment[];
  totalCount: number;
  _links: ILinks;
}>;

/** Every environment of one project in one page; the route allows up to 1000. */
export const getProjectEnvironments = (account: Account, projectKey: string, limit = 1000) =>
  gonfalon.request<EnvironmentsPage>({
    method: 'GET',
    url: `/api/v2/projects/${encodeURIComponent(projectKey)}/environments?sort=name&limit=${limit}`,
    headers: { 'LD-Api-Version': LD_API_VERSION, ...accountScopeHeaders(account) },
  });

/**
 * Fuzzy search over every environment in the account by id, key, name, project key,
 * project name, SDK key or mobile key. Unpaginated, ranked best match first.
 */
export const searchAccountEnvironments = (account: Account, term: string) =>
  gonfalon.request<EnvironmentSummary[]>({
    method: 'GET',
    url: `/internal/list/environments?filter=${encodeURIComponent(term)}`,
    headers: accountScopeHeaders(account),
  });

export type CustomRoleResponse = Readonly<{
  entities: {
    customRoles: {
      [id: string]: CustomRole;
    };
  };
}>;

export const getAccountCustomRoles = (account: Account) =>
  gonfalon.request<CustomRoleResponse>(account._links.customRoles.href, schema.customRoles);

export const getAccountRoleVersionAtTime = (account: Account, roleId: string, timestamp: number) =>
  gonfalon.request<CustomRole>({
    url: `/private/accounts/${account._id}/roles/${roleId}/version-at/${timestamp}`,
    headers: {
      'X-LD-AccountId': account._id,
      'X-LD-EnvId': account._id,
      'X-LD-PrjId': account._id,
    },
  });

export type UsageResponse = Readonly<{
  metadata: Metadata;
  series: Series;
}>;

export const putAccountOwner = (account: Account, promotion: OwnerPromotion) =>
  gonfalon.request<MemberResponse>(
    {
      method: 'PUT',
      url: `/private/accounts/${account._id}/owner`,
      body: promotion,
    },
    schema.members,
  );

export const putSubscription = (account: Account, plan: Plan) =>
  gonfalon.request<Subscription>({
    method: 'PUT',
    url: `/private/billingv2/accounts/${account._id}/subscription`,
    body: plan,
  });

export type LimitPlanStatic = {
  type: string;
  limit: number;
  name: string;
  stripeIdMonthly?: string;
  stripeIdAnnual?: string;
  amberfloId?: string;
};

export type LimitPlansResponse = {
  items: Record<string, Record<string, LimitPlanStatic>>; // items[planType][limitType]
};

export const getLimitPlans = () =>
  gonfalon.request<LimitPlansResponse>({
    method: 'GET',
    url: '/private/collections/limitplans',
  });

export const postProfileMfaDisable = (account: Account, member: Member) => {
  const body = {
    memberId: member._id,
  };
  return gonfalon.request<Member>({
    method: 'POST',
    url: `/private/accounts/${account._id}/owner/mfa/disable`,
    body: JSON.stringify(body),
  });
};

export const postMemberMfaReset = (account: Account, member: Member) => {
  const body = {
    memberId: member._id,
  };
  return gonfalon.request<Member>({
    method: 'POST',
    url: `/private/accounts/${account._id}/members/${member._id}/mfa/reset`,
    body: JSON.stringify(body),
  });
};

export const postAccountSsoDisable = (account: Account) =>
  gonfalon.request<SamlConfig>({
    method: 'POST',
    url: `/private/accounts/${account._id}/sso/disable`,
  });

export const searchMembers = (account: Account, query: string) => {
  // Handle null/undefined query
  const safeQuery = query || '';

  // Check if the query looks like a member ID (ObjectId)
  const isMemberId = isObjectIdHex(safeQuery);

  let filterParam;
  if (isMemberId) {
    // Search by member ID - use trimmed version to match isObjectIdHex validation
    const trimmedQuery = safeQuery.trim();
    const encodedQuery = encodeURIComponent(trimmedQuery);
    filterParam = `id:${encodedQuery}`;
  } else {
    // Search by text fields (names, emails, etc.) - use original query
    const encodedQuery = encodeURIComponent(safeQuery);
    filterParam = `query:${encodedQuery}`;
  }

  // Manually build the query string so the ':' after filter type is not percent-encoded
  const url = `/api/v2/members?filter=${filterParam}&limit=20&sort=displayName&expand=roleAttributes`;

  return gonfalon.request<MemberResponse>(
    {
      method: 'GET',
      url,
      headers: {
        'ld-api-version': 'beta',
        'X-LD-AccountId': account._id,
        'X-LD-EnvId': account._id,
        'X-LD-PrjId': account._id,
      },
    },
    schema.members,
  );
};

export const filterMembers = (account: Account, roleFilter: string, teamFilter: string) => {
  const filters: string[] = [];

  // Add role filter if provided
  if (roleFilter && roleFilter.trim()) {
    const encodedRole = encodeURIComponent(roleFilter.trim());
    filters.push(`role:${encodedRole}`);
  }

  // Add team filter if provided
  if (teamFilter && teamFilter.trim()) {
    const encodedTeam = encodeURIComponent(teamFilter.trim());
    filters.push(`team:${encodedTeam}`);
  }

  // Build filter parameter (comma-separated for AND logic)
  const filterParam = filters.join(',');

  // Manually build the query string so the ':' after filter type is not percent-encoded
  const url = `/api/v2/members?filter=${filterParam}&limit=20&sort=displayName&expand=roleAttributes`;

  return gonfalon.request<MemberResponse>(
    {
      method: 'GET',
      url,
      headers: {
        'ld-api-version': 'beta',
        'X-LD-AccountId': account._id,
        'X-LD-EnvId': account._id,
        'X-LD-PrjId': account._id,
      },
    },
    schema.members,
  );
};

// Fetch members from a pagination URL (for "Load more" functionality)
export const fetchMembersByUrl = (account: Account, url: string) =>
  gonfalon.request<MemberResponse>(
    {
      method: 'GET',
      url,
      headers: {
        'ld-api-version': 'beta',
        'X-LD-AccountId': account._id,
        'X-LD-EnvId': account._id,
        'X-LD-PrjId': account._id,
      },
    },
    schema.members,
  );

export const resendMemberInvitation = (account: Account, memberId: string) =>
  gonfalon.request<void>({
    url: `/api/v2/members/${memberId}/resend-invitation`,
    method: 'POST',
    headers: {
      'ld-api-version': 'beta',
      'X-LD-AccountId': account._id,
      'X-LD-EnvId': account._id,
      'X-LD-PrjId': account._id,
    },
  });

/**
 * General tab API path on the Gonfalon ALB (TSS-1951). Same origin as the app,
 * so routing through gonfalon.request (withCredentials) sends the ALB OIDC
 * cookie and no manual CORS/credentials handling is needed. gonfalon.request
 * prepends config.gonfalon.host, so this returns a host-relative path. The ALB
 * routes the path to the support-internal lambda, and that route differs by env:
 *   Staging:    terraform app-ld-com-alb.tf forward_to_lambda_patterns ["/internal/vital/*"]
 *   Production: terraform vitalstatistix_alb_authenticated_rule.tf path /vitalstatistix/action/get-general
 */
const getGeneralAccountInfoPath = () => {
  const isStaging = config.gonfalon.host.includes('ld-stg.');
  return isStaging ? '/internal/vital/get-general' : '/vitalstatistix/action/get-general';
};

/**
 * Fetches general tab data from the support-internal lambda via the Gonfalon ALB.
 * With `sections` it fetches only those sections (one section per request so each
 * loads/fails independently); without it, the lambda returns the full payload
 * (launchdarkly/eap, zendesk, integrations, supportEntitlement, dataExport,
 * resourceLimits, rateLimitIncreases).
 *
 * Routed through gonfalon.request so it shares the same withCredentials cookie
 * auth, base URL, timeout, and error handling as every other vital API call.
 * The previous hand-rolled fetch set credentials/CORS manually and only attached
 * the API key when present, so its auth behaviour could silently diverge between
 * local, staging, and prod — the class of "works locally, fails deployed" bug.
 */
export const fetchGeneralAccountInfo = (
  account: Account,
  sections?: readonly GeneralAccountSection[],
): Observable<GeneralAccountInfoResponseRaw> => {
  const apiKey = process.env.REACT_APP_EAP_SEGMENTS_API_KEY;
  return gonfalon.request<GeneralAccountInfoResponseRaw>({
    method: 'POST',
    url: getGeneralAccountInfoPath(),
    // `sections` lets the caller fetch a single section so each General-tab
    // section loads/fails independently; omitted -> the lambda returns everything.
    body: { accountId: account._id, ...(sections && sections.length > 0 ? { sections } : {}) },
    ...(apiKey ? { headers: { 'X-API-KEY': apiKey } } : {}),
  });
};

/** @deprecated Use fetchGeneralAccountInfo and use response.eap. */
export const fetchEapSegments = (account: Account) =>
  fetchGeneralAccountInfo(account).pipe(
    map(
      (res): IEapSegmentsResponse => [
        ...((res as GeneralAccountInfoResponseRaw)?.launchdarkly?.account?.eapSegments ?? []),
      ],
    ),
  );

export type TeamResponse = Readonly<Team>;

export type TeamsListResponse = Readonly<{
  items: Team[];
  totalCount: number;
  _links: ILinks;
}>;

export const getTeamWithRolesAndAttributes = (teamKey: string, account: Account) => {
  const url = `/api/v2/teams/${teamKey}?expand=roles,roleAttributes`;

  return gonfalon.request<TeamResponse>({
    method: 'GET',
    url,
    headers: {
      'ld-api-version': 'beta',
      'X-LD-AccountId': account._id,
      'X-LD-EnvId': account._id,
      'X-LD-PrjId': account._id,
    },
  });
};

export type TeamsListParams = Readonly<{
  query?: string;
  sort?: string;
  limit: number;
  offset: number;
}>;

export const getAccountTeams = (account: Account, { query, sort, limit, offset }: TeamsListParams) => {
  const params = new URLSearchParams({ expand: 'roles,members', limit: String(limit), offset: String(offset) });
  if (query) {
    params.set('query', query);
  }
  if (sort) {
    params.set('sort', sort);
  }

  return gonfalon.request<TeamsListResponse>({
    method: 'GET',
    url: `/api/v2/teams?${params.toString()}`,
    headers: {
      'ld-api-version': LD_API_VERSION,
      'X-LD-AccountId': account._id,
      'X-LD-EnvId': account._id,
      'X-LD-PrjId': account._id,
    },
  });
};

export const fetchTeamRolesByUrl = (account: Account, url: string) =>
  gonfalon.request<TeamCustomRoles>({
    method: 'GET',
    url,
    headers: {
      'ld-api-version': LD_API_VERSION,
      'X-LD-AccountId': account._id,
      'X-LD-EnvId': account._id,
      'X-LD-PrjId': account._id,
    },
  });
